import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppBar from '../../components/AppBar';
import ContentGrid from '../../components/ContentGrid';
import AppFooter from '../../components/AppFooter';
import { GlobalMicroButton } from '../../components/GlobalMicroButton';
import { useTheme } from '../../context/ThemeProvider';
import { useLocale } from '../../context/DynamicLocaleProvider';
import changelogData from '../../changelog.json';
import styles from './page.module.css';

type ChangelogEntry = {
  version: string;
  date: string;
  title: string;
  description?: string;
  changes: string[];
  tags?: string[];
};

// Static changelog data – loaded from root JSON file
const CHANGELOG_ENTRIES: ChangelogEntry[] = changelogData as ChangelogEntry[];

export default function ChangelogPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { locale, t } = useLocale();

  const formatDate = (value: string) => {
    const safeLocale = locale || 'de-DE';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(safeLocale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div>
      {/* App bar */}
      <AppBar />

      {/* Content on global grid */}
      <ContentGrid 
      mobileRows="64px auto auto 48px"
      rows="64px 1fr 48px">

        {/* Toolbar left – back + page title */}
        <div className={styles.toolbarLeft}>
          <div className={styles.backButton}>
            <GlobalMicroButton
              title={t('changelog_back_to_home')}
              icon={
                <img
                  src={isDarkMode ? "/assets/icons/chevron.left.svg" : "/assets/icons/chevron.left.black.svg"}
                  alt="Back"
                  width={8}
                  height={8}
                />
              }
              onClick={() => navigate('/home')}
            />
          </div>
          <b>{t('changelog_title')}</b>
        </div>

        {/* Toolbar right – current version badge */}
        <div className={styles.toolbarRight}>
        </div>

        {/* Main content */}
        <main className={styles.changelogContent}>
          <header className={styles.header}>
            <h1 className={styles.title}>{t('changelog_page_title')}</h1>
            <h1 className={styles.title2}>
              {t('changelog_current_version').replace('{version}', CHANGELOG_ENTRIES[0]?.version || '')}
            </h1>
            <p className={styles.subtitle}>
              {t('changelog_subtitle')}
            </p>
          </header>

          <section className={styles.releaseList} aria-label={t('changelog_aria_label')}>
            {CHANGELOG_ENTRIES.map((entry) => (
              <article key={entry.version} className={styles.release}>
                <div className={styles.releaseHeader}>
                  <span className={styles.version}>v{entry.version}</span>
                  <span className={styles.date}>{formatDate(entry.date)}</span>
                </div>

                <h2 className={styles.heading}>{entry.title}</h2>
                {entry.description && (
                  <p className={styles.description}>{entry.description}</p>
                )}

                <ul className={styles.changesList}>
                  {entry.changes.map((change) => (
                    <li key={change}>{change}</li>
                  ))}
                </ul>

                <div className={styles.metaRow}>
                  {entry.tags && entry.tags.length > 0 && (
                    <>
                      {entry.tags.map((tag) => (
                        <span key={tag} className={styles.tag}>
                          {tag}
                        </span>
                      ))}
                    </>
                  )}
                </div>
              </article>
            ))}
          </section>
        </main>

        {/* Footer */}
        <AppFooter inline />
      </ContentGrid>
    </div>
  );
}
